import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';

import { TableModule, Table, TablePageEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';

import { Api } from '../../../api/api';
import {
  adminProjectOwnersControllerGetAllProjectOwners,
  AdminProjectOwnersControllerGetAllProjectOwners$Params,
  adminProjectOwnersControllerDeleteProjectOwner,
} from '../../../api/functions';
import { ProjectOwnerResponseAdminAdminDto } from '../../../api/models/project-owner-response-admin-admin-dto';
import { HeaderService } from '../../../services/header.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';

@Component({
  selector: 'app-project-owners',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    SkeletonModule,
    ToastModule,
    TagModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './project-owners.component.html',
  styleUrl: './project-owners.component.css',
})
export class ProjectOwnersComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly projectOwners = signal<ProjectOwnerResponseAdminAdminDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly totalRecords = signal(0);
  protected readonly searchControl = new FormControl('');

  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  protected readonly queryParams = signal<AdminProjectOwnersControllerGetAllProjectOwners$Params>({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  protected first = 0;
  protected readonly rowsPerPageOptions = [10, 25, 50, 100];

  constructor() {}

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('projectOwners.title'));
    this.headerService.setSubtitle(this.translateService.instant('projectOwners.subtitle'));

    // Load initial data
    this.loadProjectOwners();

    // Setup search with debounce
    this.searchControl.valueChanges
      .pipe(skip(1), takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.isSearching = true;
        this.first = 0;
        const params = this.queryParams();
        this.queryParams.set({
          ...params,
          page: 1,
          search: searchTerm || undefined,
        });
        this.loadProjectOwners();
      });
  }

  protected onPageChange(event: TablePageEvent): void {
    const page = Math.floor((event.first || 0) / (event.rows || this.queryParams().limit!)) + 1;
    const limit = event.rows || this.queryParams().limit!;

    this.first = event.first || 0;

    const currentParams = this.queryParams();
    this.queryParams.set({
      page,
      limit,
      sortBy: currentParams.sortBy,
      sortOrder: currentParams.sortOrder,
      search: currentParams.search,
    });

    this.loadProjectOwners();
  }

  private loadProjectOwners(): void {
    this.loading.set(true);
    this.searchControl.disable();

    this.api
      .invoke(adminProjectOwnersControllerGetAllProjectOwners, this.queryParams())
      .then((response) => {
        this.projectOwners.set(response.data);
        this.totalRecords.set(response.meta.totalItems);
      })
      .catch(() => {
        this.projectOwners.set([]);
        this.totalRecords.set(0);
      })
      .finally(() => {
        this.loading.set(false);
        this.searchControl.enable();

        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  protected editProjectOwner(projectOwner: ProjectOwnerResponseAdminAdminDto): void {
    this.router.navigate(['/project-owners', projectOwner.id]);
  }

  protected addProjectOwner(): void {
    this.router.navigate(['/project-owners', 'new']);
  }

  protected refreshProjectOwners(): void {
    const params = this.queryParams();
    this.queryParams.set({
      ...params,
      page: Math.floor(this.first / params.limit!) + 1,
    });
    this.loadProjectOwners();
  }

  protected deleteProjectOwner(projectOwner: ProjectOwnerResponseAdminAdminDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('projectOwners.confirmDelete.message', {
        email: projectOwner.email,
      }),
      header: this.translateService.instant('projectOwners.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('projectOwners.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('projectOwners.confirmDelete.cancel'),
      accept: () => {
        this.api
          .invoke(adminProjectOwnersControllerDeleteProjectOwner, { id: projectOwner.id })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projectOwners.success.deleteTitle'),
              detail: this.translateService.instant('projectOwners.success.deleteMessage', {
                email: projectOwner.email,
              }),
              life: 4000,
            });
            this.refreshProjectOwners();
          })
          .catch(() => {});
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
